import { useApi } from 'contexts/Api/Api';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    UserContainer,
    UserImage,
    UserName,
    UserPicture,
    UserProfile,
    UserProfileMenu,
    UserProfileMenuButton,
    UserProfileMenuLink,
} from './UserAvatarMenu.styled';

export default function UserAvatarMenu() {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
    const api = useApi();

    const displayName = api.user?.firstName?.trim() || 'Account';
    const initial = (displayName[0] || 'A').toUpperCase();

    const handleClickOutside = useCallback(
        (e: MouseEvent) => {
            if (open && containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        },
        [open]
    );

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
    }, []);

    useEffect(() => {
        window.addEventListener('click', handleClickOutside);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('click', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClickOutside, handleKeyDown]);

    useEffect(() => {
        if (open) {
            firstMenuItemRef.current?.focus();
        }
    }, [open]);

    function handleLogout() {
        api.logout();
        setOpen(false);
    }

    if (!api.user) return null;

    return (
        <UserContainer ref={containerRef}>
            <UserProfile
                aria-expanded={open}
                aria-haspopup="true"
                aria-label="User menu"
                data-testid="user-avatar-menu-button"
                onClick={() => setOpen(!open)}
            >
                <UserName>{displayName}</UserName>
                <UserPicture>
                    {api.user?.avatar ? (
                        <UserImage alt="" aria-hidden src={api.user.avatar} />
                    ) : (
                        <span aria-hidden>{initial}</span>
                    )}
                </UserPicture>
            </UserProfile>
            {open && (
                <UserProfileMenu data-testid="user-avatar-menu" role="menu">
                    <UserProfileMenuLink
                        ref={firstMenuItemRef}
                        aria-label="My profile"
                        href="/user"
                        role="menuitem"
                        data-testid="user-avatar-menu-profile"
                    >
                        My Profile
                    </UserProfileMenuLink>
                    <UserProfileMenuButton
                        aria-label="Log out"
                        data-testid="user-avatar-menu-logout"
                        disabled={api.loading}
                        role="menuitem"
                        onClick={handleLogout}
                    >
                        Log Out
                    </UserProfileMenuButton>
                </UserProfileMenu>
            )}
        </UserContainer>
    );
}
